import type { HealthSyncStatus } from '../types/healthSync';
import type { Work } from '../types/work';
import type { HealthRecord, HealthTrendsResponse } from '../types/health';
import type { AccessSession, Role, TokenRecord } from '../types/token';
import type { MochiGrowthResponse, MochiVaccinationsResponse } from '../types/mochi';
import type {
  MochiConversationIndex,
  MochiConversationWeek,
  MochiStoryLibrary,
  MochiStoryProgress,
} from '../types/mochiEnglish';
import type {
  CocoHomeDevicesResponse,
  CocoHomePairingCode,
  CreateCocoHomePairingInput,
} from '../types/cocohome';
import { clearSession, getSession } from './auth';

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function request<T>(
  method: 'GET' | 'POST' | 'DELETE',
  path: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const session = getSession();
  if (session?.kind === 'access') headers.Authorization = `Bearer ${session.token}`;
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  const res = await fetch(path, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });

  if (res.status === 401) {
    clearSession();
    const detail = await safeError(res);
    throw new ApiError(401, detail);
  }
  if (!res.ok) {
    const detail = await safeError(res);
    throw new ApiError(res.status, detail);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

async function requestBlob(path: string): Promise<Blob> {
  const headers: Record<string, string> = { Accept: '*/*' };
  const session = getSession();
  if (session?.kind === 'access') headers.Authorization = `Bearer ${session.token}`;

  const res = await fetch(path, { method: 'GET', headers });

  if (res.status === 401) {
    clearSession();
    const detail = await safeError(res);
    throw new ApiError(401, detail);
  }
  if (!res.ok) {
    const detail = await safeError(res);
    throw new ApiError(res.status, detail);
  }
  return res.blob();
}

async function safeError(res: Response): Promise<string> {
  try {
    const data = (await res.json()) as {
      error?: string | { message?: unknown; code?: unknown };
      message?: unknown;
    };
    if (typeof data.error === 'string') return data.error;
    if (
      typeof data.error === 'object' &&
      data.error !== null &&
      typeof data.error.message === 'string'
    ) {
      return data.error.message;
    }
    if (typeof data.message === 'string') return data.message;
  } catch {
    // ignore
  }
  return `${res.status} ${res.statusText}`;
}

function toAccessSession(session: Omit<AccessSession, 'kind'>): AccessSession {
  return { ...session, kind: 'access' };
}

export const api = {
  passwordStatus: () =>
    request<{ configured: boolean; setup_allowed: boolean }>('GET', '/api/auth/password-status'),
  login: async (password: string, setup = false) =>
    toAccessSession(await request<Omit<AccessSession, 'kind'>>('POST', '/api/auth/login', { password, setup })),
  me: async () =>
    toAccessSession(await request<Omit<AccessSession, 'kind'>>('GET', '/api/auth/me')),
  logout: () => request<void>('POST', '/api/auth/logout'),
  works: () => request<Work[]>('GET', '/api/works'),
  healthRecords: {
    list: () => request<HealthRecord[]>('GET', '/api/health-records'),
    trends: () => request<HealthTrendsResponse>('GET', '/api/health-records/trends'),
    file: (path: string) =>
      requestBlob(`/api/health-records/file?path=${encodeURIComponent(path)}`),
  },
  mochiGrowth: () => request<MochiGrowthResponse>('GET', '/api/mochi-growth'),
  mochiVaccinations: () => request<MochiVaccinationsResponse>('GET', '/api/mochi-vaccinations'),
  mochiEnglish: {
    conversations: () => request<MochiConversationIndex>('GET', '/api/mochi-english/conversations'),
    conversationWeek: (week: number, signal?: AbortSignal) =>
      request<MochiConversationWeek>('GET', `/api/mochi-english/conversations/weeks/${week}`, undefined, signal),
    stories: () => request<MochiStoryLibrary>('GET', '/api/mochi-english/stories'),
    setStoryCompleted: (storyId: string, completed: boolean) =>
      request<MochiStoryProgress>('POST', `/api/mochi-english/stories/${encodeURIComponent(storyId)}/completion`, { completed }),
  },
  cocoHome: {
    healthStatus: (signal?: AbortSignal) =>
      request<HealthSyncStatus>('GET', '/v1/admin/health/status', undefined, signal),
    devices: (signal?: AbortSignal) =>
      request<CocoHomeDevicesResponse>('GET', '/v1/admin/devices', undefined, signal),
    createPairing: (input: CreateCocoHomePairingInput) =>
      request<CocoHomePairingCode>('POST', '/v1/admin/pairing-codes', {
        device_type: 'ios',
        ...input,
      }),
  },
  tokens: {
    list: () => request<TokenRecord[]>('GET', '/api/tokens'),
    create: (input: { company: string; role: Role; token?: string }) =>
      request<TokenRecord>('POST', '/api/tokens', input),
    revoke: (token: string) =>
      request<void>('DELETE', `/api/tokens/${encodeURIComponent(token)}`),
  },
};
